import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  ForwardedRef
} from 'react';
import { Loading, Overlay, Text } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { usePrevious } from '../../../hooks/usePrevious';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useAddressesQuery, RestAddressAttributes, RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { LoadingBalls } from '../../LoadingBalls';
import { ShippingAddressPreview } from '../ShippingAddressPreview';
import { ShippingAddressForm } from '../ShippingAddressForm';
import { ShippingAddressList } from '../ShippingAddressList';

export enum Mode {
  Resting,
  Selecting,
  Creating,
  Editing,
  Saving
}

type Props = {
  theme?: 'simple' | 'box';
  source?: string;
  readOnly?: boolean;
  disabledAddressIds?: string[];
  defaultAddress?: RestAddressAttributes;
  defaultAddressId?: string;
  onModeChange?: (previousMode: Mode | undefined, nextMode: Mode) => void;
  onSubmit: (address: RestAddress) => Promise<void>;
};

export type RefInterface = {
  change: () => void;
};

export const ShippingAddressSelector = forwardRef(
  (
    {
      theme = 'simple',
      source,
      readOnly,
      disabledAddressIds,
      defaultAddressId,
      defaultAddress,
      onSubmit,
      onModeChange
    }: Props,
    ref: ForwardedRef<RefInterface>
  ) => {
    const { showErrorModal } = useErrorModal();
    const { addresses: allAddresses, fetched, error } = useAddressesQuery();

    const addresses = useMemo(
      () =>
        source
          ? allAddresses.filter(address => address.attributes.source === source)
          : allAddresses,
      [allAddresses, source]
    );

    const defaultMode =
      defaultAddressId || defaultAddress
        ? Mode.Resting
        : fetched && addresses.length === 0
        ? Mode.Creating
        : Mode.Selecting;

    const [mode, setMode] = useState(defaultMode);
    const [editingAddressId, setEditingAddressId] = useState<string>();

    const previousMode = usePrevious(mode);

    const selectedAddress =
      defaultAddress ?? addresses.find(address => address.id === defaultAddressId)?.attributes;
    const editingAddress = addresses.find(address => address.id === editingAddressId);

    const handleSubmit = useCallback(
      async (address: RestAddress) => {
        try {
          await onSubmit(address);
        } catch (error) {
          reportError(error);
          showErrorModal(error);
        }
        setMode(Mode.Resting);
      },
      [showErrorModal, onSubmit]
    );

    const handleChange = useCallback(
      () => !readOnly && setMode(addresses.length > 0 ? Mode.Selecting : Mode.Creating),
      [readOnly, addresses]
    );

    useImperativeHandle(ref, () => ({
      change: handleChange
    }));

    useEffect(() => {
      onModeChange?.(previousMode, mode);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    if (error) throwQueryError(error);

    if (!fetched) return <Loading />;

    if (mode === Mode.Resting && selectedAddress)
      return (
        <Box borderless={theme === 'simple'}>
          <ShippingAddressPreview
            attributes={selectedAddress}
            onChange={readOnly ? undefined : handleChange}
          />
        </Box>
      );

    if (mode === Mode.Creating)
      return (
        <ShippingAddressForm
          submitButtonLabel="Continue"
          onCancel={addresses.length === 0 ? undefined : () => setMode(Mode.Selecting)}
          onSave={address => address && handleSubmit(address)}
        />
      );

    if (mode === Mode.Editing)
      return (
        <ShippingAddressForm
          address={editingAddress}
          submitButtonLabel="Continue"
          onCancel={() => setMode(Mode.Selecting)}
          onSave={address => address && handleSubmit(address)}
        />
      );

    return (
      <>
        <ShippingAddressList
          addresses={addresses}
          disabledAddressIds={disabledAddressIds}
          defaultAddressId={defaultAddressId}
          onAdd={() => setMode(Mode.Creating)}
          onEdit={addressId => {
            setEditingAddressId(addressId);
            setMode(Mode.Editing);
          }}
          onCancel={() => setMode(Mode.Resting)}
          onChange={addressId => {
            const address = addresses.find(address => address.id === addressId);
            if (!address) return;
            setMode(Mode.Saving);
            handleSubmit(address);
          }}
        />
        {mode === Mode.Saving && (
          <Overlay>
            <LoadingBalls />
            <Text treatment="eyebrow">Saving</Text>
          </Overlay>
        )}
      </>
    );
  }
);
